import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, Inbox, LifeBuoy, Users, Loader2, ChevronRight, MapPin, Calendar,
  Sparkles, MessageSquarePlus,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateMonthDay } from '../../utils/timeFormat';

/**
 * PO-2026-06 — Unified talent Messages center.
 *
 * One inbox for ALL talent communication. The top-nav message icon opens this
 * with "All Messages" by default. Views:
 *   • All Messages   — iEvntür Support + Event Channels, chronological
 *   • iEvntür Support — the canonical talent↔admin conversation (AI + human
 *                       escalation live in this same thread)
 *   • Event Channels  — per-shift group conversations
 * No duplicate tabs / no duplicate threads pointing at the same conversation.
 */
const fmtTime = (s) => {
  if (!s) return '';
  const d = new Date(s);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
};

const TABS = [
  { key: 'all', label: 'All Messages', icon: Inbox },
  { key: 'support', label: 'iEvntür Support', icon: LifeBuoy },
  { key: 'channels', label: 'Event Channels', icon: Users },
];

const UnifiedMessages = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const initialTab = TABS.some((t) => t.key === params.get('tab')) ? params.get('tab') : 'all';
  const [tab, setTab] = useState(initialTab);
  const [threads, setThreads] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, c] = await Promise.all([
        api.get('/messages/onsite/threads').catch(() => ({ data: { threads: [] } })),
        api.get('/messages/channels').catch(() => ({ data: [] })),
      ]);
      setThreads(t.data?.threads || []);
      setChannels(Array.isArray(c.data) ? c.data : []);
    } catch (e) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const changeTab = (k) => {
    setTab(k);
    const next = new URLSearchParams(params);
    if (k === 'all') next.delete('tab'); else next.set('tab', k);
    setParams(next, { replace: true });
  };

  const supportLabel = (th) => (th.event_title || th.venue_name
    ? `iEvntür Support · ${th.event_title || th.venue_name}`
    : 'iEvntür Support');

  const openSupportConversation = async () => {
    if (opening) return;
    setOpening(true);
    try {
      const r = await api.post('/messages/onsite/open-admin');
      navigate(`/talent/support?thread=${r.data.thread_id}`);
    } catch (e) {
      navigate('/talent/support');
    } finally {
      setOpening(false);
    }
  };

  // Unified, chronological list for "All Messages".
  const allItems = useMemo(() => {
    const s = threads.map((th) => ({
      kind: 'support',
      id: `s-${th.thread_id}`,
      title: supportLabel(th),
      preview: th.last_message || 'Start a conversation with iEvntür Support',
      at: th.last_message_at,
      unread: th.unread_count || 0,
      onOpen: () => navigate(`/talent/support?thread=${th.thread_id}`),
    }));
    const c = channels.map((ch) => ({
      kind: 'channel',
      id: `c-${ch.id}`,
      title: ch.title,
      subtitle: [ch.venue_name, ch.shift_date ? formatDateMonthDay(ch.shift_date) : null].filter(Boolean).join(' · '),
      preview: ch.last_message_preview || 'No messages yet',
      at: ch.last_message_at || ch.created_at,
      unread: ch.unread_count || 0,
      channelType: ch.channel_type,
      onOpen: () => navigate(`/messages/channel/${ch.id}`),
    }));
    return [...s, ...c].sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0));
  }, [threads, channels]); // eslint-disable-line react-hooks/exhaustive-deps

  const supportUnread = threads.reduce((n, t) => n + (t.unread_count || 0), 0);
  const channelsUnread = channels.reduce((n, c) => n + (c.unread_count || 0), 0);

  const Row = ({ item }) => (
    <button
      type="button"
      onClick={item.onOpen}
      className="w-full text-left bg-white border border-slate-200 rounded-xl p-3.5 flex items-center gap-3 hover:border-cyan-300 hover:shadow-sm transition-all"
      data-testid={`unified-msg-row-${item.id}`}
    >
      <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${
        item.kind === 'support'
          ? 'bg-gradient-to-br from-orange-500 to-amber-500'
          : 'bg-gradient-to-br from-cyan-500 to-blue-500'
      }`}>
        {item.kind === 'support' ? <LifeBuoy className="w-5 h-5 text-white" /> : <Users className="w-5 h-5 text-white" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-slate-900 truncate text-sm">{item.title}</span>
          {item.kind === 'support' && (
            <Badge className="bg-indigo-50 text-indigo-600 border-0 text-[9px] px-1.5 py-0 gap-0.5">
              <Sparkles className="w-2.5 h-2.5" /> AI
            </Badge>
          )}
          {item.channelType && item.channelType === 'emergency' && (
            <Badge className="bg-rose-100 text-rose-700 border-0 text-[9px]">urgent</Badge>
          )}
        </div>
        {item.subtitle && (
          <div className="text-[11px] text-slate-400 truncate">{item.subtitle}</div>
        )}
        <div className="text-xs text-slate-500 truncate mt-0.5">{item.preview}</div>
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        <span className="text-[10px] text-slate-400">{fmtTime(item.at)}</span>
        {item.unread > 0
          ? <Badge className="bg-rose-500 text-white border-0 text-[10px] min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1" data-testid={`unified-unread-${item.id}`}>{item.unread}</Badge>
          : <ChevronRight className="w-4 h-4 text-slate-300" />}
      </div>
    </button>
  );

  const EmptyState = ({ icon: Icon, title, subtitle }) => (
    <div className="bg-white border border-slate-200 rounded-xl py-16 text-center text-slate-500" data-testid="unified-empty">
      <Icon className="w-12 h-12 mx-auto mb-3 text-slate-300" />
      <div className="font-medium text-slate-700">{title}</div>
      <div className="text-xs mt-1 max-w-xs mx-auto">{subtitle}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="unified-messages-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 pt-3">
          <div className="flex items-center gap-3 pb-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/talent/home')} data-testid="unified-back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-lg font-bold text-slate-900 flex items-center gap-2 flex-1">
              <Inbox className="w-5 h-5 text-cyan-600" /> Messages
            </h1>
            <Button
              size="sm"
              onClick={openSupportConversation}
              disabled={opening}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="unified-message-support-btn"
            >
              {opening ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquarePlus className="w-4 h-4 mr-1" />}
              <span className="hidden sm:inline">Message Support</span>
            </Button>
          </div>
          <div className="flex gap-1 -mb-px">
            {TABS.map((t) => {
              const active = tab === t.key;
              const count = t.key === 'support' ? supportUnread : t.key === 'channels' ? channelsUnread : (supportUnread + channelsUnread);
              return (
                <button
                  key={t.key}
                  onClick={() => changeTab(t.key)}
                  className={`relative px-3 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
                    active ? 'border-cyan-600 text-cyan-700' : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                  data-testid={`unified-tab-${t.key}`}
                >
                  <t.icon className="w-4 h-4" />
                  <span>{t.label}</span>
                  {count > 0 && (
                    <Badge className="bg-rose-500 text-white border-0 text-[9px] min-w-[16px] h-4 flex items-center justify-center rounded-full px-1">{count > 99 ? '99+' : count}</Badge>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-4 space-y-2.5">
        {loading ? (
          <div className="text-center py-16 text-slate-400"><Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…</div>
        ) : tab === 'all' ? (
          allItems.length === 0
            ? <EmptyState icon={Inbox} title="You're all caught up" subtitle="Direct messages with iEvntür Support and your event channels will show up here." />
            : allItems.map((item) => <Row key={item.id} item={item} />)
        ) : tab === 'support' ? (
          <>
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-3.5 flex items-start gap-3" data-testid="unified-support-banner">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shrink-0">
                <LifeBuoy className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-orange-900 text-sm flex items-center gap-1.5">
                  iEvntür Support
                  <Badge className="bg-indigo-100 text-indigo-700 border-0 text-[9px] gap-0.5"><Sparkles className="w-2.5 h-2.5" /> Evie · Virtual Assistant</Badge>
                </div>
                <p className="text-xs text-orange-800/80 mt-0.5">Ask Evie anything — instant answers from your real account details, with team review when it&apos;s needed.</p>
              </div>
              <Button size="sm" onClick={openSupportConversation} disabled={opening} className="bg-orange-600 hover:bg-orange-700 text-white shrink-0" data-testid="unified-open-support-btn">
                {opening ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Open'}
              </Button>
            </div>
            {threads.length === 0
              ? <div className="text-center text-xs text-slate-400 py-6">No support conversations yet — tap Open to start one.</div>
              : threads.map((th) => (
                <Row key={`s-${th.thread_id}`} item={{
                  kind: 'support', id: `s-${th.thread_id}`, title: supportLabel(th),
                  preview: th.last_message || 'Start a conversation with iEvntür Support',
                  at: th.last_message_at, unread: th.unread_count || 0,
                  onOpen: () => navigate(`/talent/support?thread=${th.thread_id}`),
                }} />
              ))}
          </>
        ) : (
          channels.length === 0
            ? <EmptyState icon={Users} title="No event channels yet" subtitle="Once you're booked for a gig, an event channel appears here so you can chat with the client and team." />
            : channels.map((ch) => (
              <Row key={`c-${ch.id}`} item={{
                kind: 'channel', id: `c-${ch.id}`, title: ch.title,
                subtitle: [ch.venue_name, ch.shift_date ? formatDateMonthDay(ch.shift_date) : null].filter(Boolean).join(' · '),
                preview: ch.last_message_preview || 'No messages yet',
                at: ch.last_message_at || ch.created_at, unread: ch.unread_count || 0,
                channelType: ch.channel_type,
                onOpen: () => navigate(`/messages/channel/${ch.id}`),
              }} />
            ))
        )}
      </div>
    </div>
  );
};

export default UnifiedMessages;
